import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, computed, effect, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CardModule } from 'primeng/card';
import { MultiSelectModule } from 'primeng/multiselect';
import { Subject, takeUntil } from 'rxjs';
import { BaseChartDirective } from 'ng2-charts';
import { ChartData, ChartOptions } from 'chart.js';
import { CallGraphData } from 'src/app/api/types/recruiting';
import { DashboardDataService } from 'src/app/routed-modules/mission-control/services/dashboard-data.service';

@Component({
  selector: 'app-call-stats-graph',
  standalone: true,
  imports: [CommonModule, CardModule, MultiSelectModule, FormsModule, BaseChartDirective],
  templateUrl: './call-stats-graph.component.html',
  styleUrl: './call-stats-graph.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CallStatsGraphComponent implements OnInit, OnDestroy {
  private ngUnsubscribe = new Subject<void>();

  // Loading state
  isLoading = signal<boolean>(true);

  // Years for selection
  availableYears = computed(() => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = 2008; year <= currentYear + 1; year++) {
      years.push({ label: year.toString(), value: year });
    }
    return years;
  });

  selectedYears = signal<number[]>([new Date().getFullYear(), new Date().getFullYear() - 1]);

  // Chart configuration
  chartData = signal<ChartData<'line', number[], string>>({
    labels: [],
    datasets: []
  });

  chartOptions: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          label: function(context) {
            const dataset = context.dataset;
            const value = dataset.data[context.dataIndex];
            return `${dataset.label}: ${value?.toLocaleString() ?? '0'}`;
          }
        }
      }
    },
    hover: {
      mode: 'x'
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value) {
            return value.toLocaleString();
          }
        }
      }
    }
  };

  constructor(private dashboardDataService: DashboardDataService) {

    effect(() => {
      const currentData = this.dashboardDataService.callGraphData();
      if (currentData && currentData.length > 0) {
        this.loadGraphData(currentData);
      }
    })

    // Watch for changes to selectedYears using effect
    effect(() => {
      this.updateChartData(this.selectedYears());
    });
  }

  ngOnInit() {
    // Initialize data loading
    this.dashboardDataService.initializeData();

  }

  private loadGraphData(graphData: any[]) {
    this.isLoading.set(true);
    
    // Process the graph data and update chart
    this.processGraphData(graphData);
    
    this.isLoading.set(false);
    
    // Force chart update after a short delay to ensure DOM is ready
    setTimeout(() => {
      this.chartData.update(current => ({ ...current }));
    }, 100);
  }

  private processGraphData(data: any[]) {
    // Group data by year and month
    const yearDataMap = new Map<number, number[]>();
    
    data.forEach(item => {
      // CallGraphData has month: string and total: number
      if (item.month && item.total !== undefined) {
        // Parse month string (e.g., "2024-01" or "Jan 2024")
        const monthStr = item.month.toString();
        let year: number;
        let month: number;
        
                 if (monthStr.includes('-')) {
           // Format: "2024-01"
           const [yearStr, monthPart] = monthStr.split('-');
           year = parseInt(yearStr);
           month = parseInt(monthPart);
        } else if (monthStr.includes(' ')) {
          // Format: "Jan 2024"
          const parts = monthStr.split(' ');
          const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
          const monthIndex = monthNames.indexOf(parts[0]);
          year = parseInt(parts[1]);
          month = monthIndex + 1;
        } else {
          // Try to parse as just a year
          year = parseInt(monthStr);
          month = 1; // Default to January
        }
        
        if (!isNaN(year) && !isNaN(month) && month >= 1 && month <= 12) {
          if (!yearDataMap.has(year)) {
            yearDataMap.set(year, Array(12).fill(0));
          }
          const monthArray = yearDataMap.get(year);
          if (monthArray) {
            monthArray[month - 1] = item.total;
          }
        }
      }
    });

    // Update chart data
    this.updateChartDataWithRealData(yearDataMap);
  }

  private updateChartDataWithRealData(yearDataMap: Map<number, number[]>) {
    const currentYear = new Date().getFullYear();
    
    // Match ColdFusion: Create a rolling 12-month window
    // ColdFusion shows data starting from September (current month) and going back 12 months
    const monthOrder = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    // Get current month (0-11)
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth();
    
    // Create labels for the last 12 months starting from current month
    const labels: string[] = [];
    for (let i = 11; i >= 0; i--) {
      const monthIndex = (currentMonth - i + 12) % 12;
      labels.push(monthOrder[monthIndex]);
    }

    const datasets = this.selectedYears().map(year => {
      const colors = this.getYearColor(year, currentYear);
      
      // Map the year data to the rolling 12-month window
      const mappedData = labels.map(monthName => {
        const monthIndex = monthOrder.indexOf(monthName);
        const yearData = yearDataMap.get(year);
        return yearData && yearData[monthIndex] ? yearData[monthIndex] : 0;
      });
      
      return {
        data: mappedData,
        label: year === currentYear ? 'This Year' : year === currentYear - 1 ? 'Previous Year' : `${year}`,
        ...colors,
        tension: 0.4,
        fill: true
      };
    });

    const chartData = {
      labels: labels,
      datasets: datasets
    };

    this.chartData.set(chartData);
  }

  private getYearColor(year: number, currentYear: number): { 
    backgroundColor: string;
    borderColor: string;
    pointBackgroundColor: string;
    pointBorderColor: string;
    pointHoverBackgroundColor: string;
    pointHoverBorderColor: string;
  } {
    if (year === currentYear) {
      // Match ColdFusion: "This Year" - orange colors
      return {
        backgroundColor: 'rgba(232, 123, 38, .2)',
        borderColor: 'rgba(232, 123, 38, .7)',
        pointBackgroundColor: 'rgba(232, 123, 38, .7)',
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: 'rgba(232, 123, 38, .7)',
        pointHoverBorderColor: 'rgba(232, 123, 38, .7)'
      };
    } else if (year === currentYear - 1) {
      // Match ColdFusion: "Previous Year" - gray colors
      return {
        backgroundColor: 'rgba(150,150,150,0.1)',
        borderColor: 'rgba(150,150,150,.8)',
        pointBackgroundColor: 'rgba(150,150,150,.8)',
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: 'rgba(150,150,150,.8)',
        pointHoverBorderColor: 'rgba(150,150,150,.8)'
      };
    } else {
      // For older years, use a gradient of gray with decreasing opacity
      const opacity = Math.max(0.3, 1 - (currentYear - year) * 0.1);
      return {
        backgroundColor: `rgba(150,150,150,${opacity * 0.1})`,
        borderColor: `rgba(150,150,150,${opacity})`,
        pointBackgroundColor: `rgba(150,150,150,${opacity})`,
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: `rgba(150,150,150,${opacity})`,
        pointHoverBorderColor: `rgba(150,150,150,${opacity})`
      };
    }
  }

  private updateChartData(years: number[]): void {
    // This method is called when years selection changes
    // The actual data processing is handled in processGraphData
    // We just need to trigger a re-render with the current data
    const currentData = this.dashboardDataService.callGraphData();
    if (currentData) {
      this.processGraphData(currentData);
    }
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
} 